import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import superagent from 'superagent';

import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {

  createColumnHelper,

} from '@tanstack/react-table';
import { Card, CardContent } from '@shadcn/components/ui/card';

import { Button } from '@shadcn/components/ui/button';
import { 
  Dialog,
  DialogContent 
} from '@shadcn/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@shadcn/components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shadcn/components/ui/tabs";
import { Calendar } from "@shadcn/components/ui/calendar";
import { Slider } from "@shadcn/components/ui/slider";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import moment from 'moment';
import { backendUrl } from '@helpers/consts';
import ProductVariantMultiSelector from '../helpers/ProductVariantMultiSelector';
import useQuery from '@helpers/JIFI/useQuery';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend
} from 'chart.js';


// Import components
import ProductAccuracyChart from './components/ProductAccuracyChart';
import LocationAccuracyChart from './components/LocationAccuracyChart';
import HitRateGauges from './components/HitRateGauges';
import VarianceHeatMap from './components/VarianceHeatMap';
import TopIssuesLists from './components/TopIssuesLists';
import EnhancedTrendChart from './components/EnhancedTrendChart';
import TimeBasedReportChart from './components/TimeBasedReportChart';
import DetailedInvestigationView from './components/DetailedInvestigationView';
import ToleranceSettingsModal from './components/ToleranceSettingsModal';
import AlertDashboard from './components/AlertDashboard';



// Đăng ký ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  ChartTooltip,
  ChartLegend,
  Title
);


const DualRangeSlider = React.memo(({ min = 0, max = 100, value = [0, 100], onChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);

  const handlePointerDown = useCallback((e, thumb) => {
    e.preventDefault();
    setIsDragging(thumb);
    
    const getClientX = (e) => e.touches ? e.touches[0].clientX : e.clientX;
    
    const handlePointerMove = (e) => {
      if (!sliderRef.current) return;
      
      const rect = sliderRef.current.getBoundingClientRect();
      const clientX = getClientX(e);
      const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const newValue = Math.round(percentage);
      
      const newRange = [...value];
      if (thumb === 'min') {
        newRange[0] = Math.min(newValue, value[1] - 1);
      } else {
        newRange[1] = Math.max(newValue, value[0] + 1);
      }
      onChange(newRange);
    };
    
    const handlePointerUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('touchend', handlePointerUp);
    };
    
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchmove', handlePointerMove);
    document.addEventListener('touchend', handlePointerUp);
  }, [value, onChange]);

  return (
    <div className="relative w-full h-6 flex items-center">
      {/* Track */}
      <div 
        ref={sliderRef}
        className="relative w-full h-2 bg-gray-200 rounded-full cursor-pointer"
      >
        {/* Active range */}
        <div 
          className="absolute h-2 bg-gray-900 rounded-full"
          style={{
            left: `${value[0]}%`,
            width: `${value[1] - value[0]}%`
          }}
        />
        
        {/* Min thumb */}
        <div
          className={`absolute w-5 h-5 bg-white border-2 border-gray-900 rounded-full cursor-pointer transform -translate-y-1/2 -translate-x-1/2 transition-shadow ${
            isDragging === 'min' ? 'shadow-lg scale-110' : 'hover:shadow-md'
          }`}
          style={{ left: `${value[0]}%`, top: '50%' }}
          onMouseDown={(e) => handlePointerDown(e, 'min')}
          onTouchStart={(e) => handlePointerDown(e, 'min')}
        />
        
        {/* Max thumb */}
        <div
          className={`absolute w-5 h-5 bg-white border-2 border-gray-900 rounded-full cursor-pointer transform -translate-y-1/2 -translate-x-1/2 transition-shadow ${
            isDragging === 'max' ? 'shadow-lg scale-110' : 'hover:shadow-md'
          }`}
          style={{ left: `${value[1]}%`, top: '50%' }}
          onMouseDown={(e) => handlePointerDown(e, 'max')}
          onTouchStart={(e) => handlePointerDown(e, 'max')}
        />
      </div>
      
      {/* Value labels */}
      <div className="absolute -bottom-6 left-0 right-0 flex justify-between text-xs text-gray-600">
        <span>{value[0]}%</span>
        <span>{value[1]}%</span>
      </div>
    </div>
  );
});

const InventoryAccuracyPage = ({ match: { params } }) => {
  const [data, setData] = useState([]);
  const [productAccuracy, setProductAccuracy] = useState([]);
  const [locationAccuracy, setLocationAccuracy] = useState([]);
  const [timeBasedReport, setTimeBasedReport] = useState([]);
  
  // New backend fields state
  const [varianceAnalysis, setVarianceAnalysis] = useState(null);
  const [hitRateMetrics, setHitRateMetrics] = useState(null);
  const [toleranceMetrics, setToleranceMetrics] = useState(null);
  const [valueWeightedAccuracy, setValueWeightedAccuracy] = useState(null);
  const [trendMetrics, setTrendMetrics] = useState(null);
  const [problemAnalysis, setProblemAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("skuLevel");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0
  });
  
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownType, setDrillDownType] = useState(null);
  const [navigationStack, setNavigationStack] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  // Set default dates: end date = today, start date = 1 month ago
  const [startDate, setStartDate] = useState(() => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return oneMonthAgo;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [activeChartType, setActiveChartType] = useState('bar');
  
  const [activeIssueTab, setActiveIssueTab] = useState('skus');
  const [showToleranceModal, setShowToleranceModal] = useState(false);
  
  // Accuracy range filter for SKU Level Analysis
  const [accuracyRange, setAccuracyRange] = useState([0, 100]);
  const [tempAccuracyRange, setTempAccuracyRange] = useState([0, 100]);
  const accuracyTimeoutRef = useRef(null);

  // Handle accuracy range change with debounce
  const handleAccuracyRangeChange = useCallback((newRange) => {
    setTempAccuracyRange(newRange);
    
    // Clear existing timeout
    if (accuracyTimeoutRef.current) {
      clearTimeout(accuracyTimeoutRef.current);
    }
    
    // Set new timeout to update actual range after user stops dragging
    accuracyTimeoutRef.current = setTimeout(() => {
      setAccuracyRange(newRange);
    }, 500); // 500ms delay
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (accuracyTimeoutRef.current) {
        clearTimeout(accuracyTimeoutRef.current);
      }
    };
    }, []);
  
  const [productData, productState] = useQuery({
    url: `/product`,
  });

  const [locationData, locationState] = useQuery({
    url: `/inventory/getApprovalLocations`,
  });

  const products = productData?.data ?? [];
  const locations = locationData ?? [];
  console.log('Location data:', locations);
  const columnHelper = createColumnHelper();

  useEffect(() => {
    if (!activeTab) {
      setActiveTab("skuLevel");
    }
  }, [activeTab]);

  const fetchInventoryAccuracyOverview = useCallback(() => {
    setLoading(true);
    
    const queryParams = {};
    
    if (selectedVariant) {
      if (Array.isArray(selectedVariant)) {
        queryParams.skus = selectedVariant.map(v => v.sku).join(',');
      } else {
        queryParams.sku = selectedVariant.sku;
      }
    } else if (selectedProduct) {
      queryParams.productId = selectedProduct._id;
    }
    
    if (selectedLocation) {
      queryParams.locationId = selectedLocation._id;
    }
    
    if (startDate) {
      queryParams.startDate = format(startDate, 'yyyy-MM-dd');
    }
    
    if (endDate) {
      queryParams.endDate = format(endDate, 'yyyy-MM-dd');
    }

    superagent
      .get(`${backendUrl}/inventory/inventoryAccuracyOverview`)
      .query(queryParams)
      .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
      .set('accept', 'json')
      .then(response => {
        const { 
          productAccuracy: newProductAccuracy, 
          locationAccuracy: newLocationAccuracy,
          timeBasedReport: newTimeBasedReport,
          // New backend fields
          varianceAnalysis: newVarianceAnalysis,
          hitRateMetrics: newHitRateMetrics,
          toleranceMetrics: newToleranceMetrics,
          valueWeightedAccuracy: newValueWeightedAccuracy,
          trendMetrics: newTrendMetrics,
          problemAnalysis: newProblemAnalysis
        } = response.body;
        
        // Set existing data
        setProductAccuracy(newProductAccuracy || []);
        setLocationAccuracy(newLocationAccuracy || []);
        setTimeBasedReport(newTimeBasedReport || []);
        
        // Set new backend fields
        setVarianceAnalysis(newVarianceAnalysis || null);
        setHitRateMetrics(newHitRateMetrics || null);
        setToleranceMetrics(newToleranceMetrics || null);
        setValueWeightedAccuracy(newValueWeightedAccuracy || null);
        setTrendMetrics(newTrendMetrics || null);
        setProblemAnalysis(newProblemAnalysis || null);
      })
      .catch(error => {
        console.error('Error fetching inventory accuracy overview:', error);
      });
  }, [selectedProduct, selectedVariant, selectedLocation, startDate, endDate]);

  const fetchInventoryAccuracy = useCallback((isLoadMore = false) => {
    setLoading(true);
    
    const queryParams = {
      page: isLoadMore ? pagination.current + 1 : 1,
      limit: pagination.pageSize,
    };
    
    if (selectedVariant) {
      if (Array.isArray(selectedVariant)) {
        queryParams.skus = selectedVariant.map(v => v.sku).join(',');
      } else {
        queryParams.sku = selectedVariant.sku;
      }
    } else if (selectedProduct) {
      queryParams.productId = selectedProduct._id;
    }
    
    if (selectedLocation) {
      queryParams.locationId = selectedLocation._id;
    }
    
    if (startDate) {
      queryParams.startDate = format(startDate, 'yyyy-MM-dd');
    }
    
    if (endDate) {
      queryParams.endDate = format(endDate, 'yyyy-MM-dd');
    }

    // Add accuracy range filters for SKU Level Analysis only
    if (activeTab === 'skuLevel' && accuracyRange[0] > 0) {
      queryParams.minAccuracy = accuracyRange[0];
    }
    if (activeTab === 'skuLevel' && accuracyRange[1] < 100) {
      queryParams.maxAccuracy = accuracyRange[1];
    }

    superagent
      .get(`${backendUrl}/inventory/inventoryAccuracyTrends`)
      .query(queryParams)
      .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
      .set('accept', 'json')
      .then(response => {
        const { 
          data: newData,
          total, 
          page, 
          totalPages 
        } = response.body;
        
        console.log('Trends Response Data:', response.body);
        console.log('Current pagination:', pagination);
        console.log('Load more?', isLoadMore, 'Page:', page);
        
        if (!newData || newData.length === 0 || page >= totalPages) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        if (isLoadMore) {
          setData(prevData => [...prevData, ...newData]);
        } else {
          setData(newData || []);
        }
        
        setPagination({
          current: page,
          pageSize: pagination.pageSize,
          total: total || 0,
          totalPages: totalPages || 1
        });
        
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching inventory accuracy trends:', error);
        setLoading(false);
      });
  }, [pagination.pageSize, pagination.current, selectedProduct, selectedVariant, selectedLocation, startDate, endDate, activeTab, accuracyRange]);

  useEffect(() => {
    fetchInventoryAccuracy(false);
    fetchInventoryAccuracyOverview();
  }, [pagination.pageSize, selectedProduct, selectedVariant, selectedLocation, startDate, endDate, accuracyRange]);

  const handleLoadMore = () => {
    fetchInventoryAccuracy(true);
  };

  const handleResetFilters = () => {
    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedLocation(null);
    setAccuracyRange([0, 100]);
    setTempAccuracyRange([0, 100]);
    // Clear any pending timeout
    if (accuracyTimeoutRef.current) {
      clearTimeout(accuracyTimeoutRef.current);
    }
    // Reset to default dates: 1 month ago to today
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    setStartDate(oneMonthAgo);
    setEndDate(new Date());
  };

  // Drill-down functionality
  const handleDrillDown = async (type, itemData) => {
    try {
      console.log('Drilling down into:', type, itemData);
      
      // Add current state to navigation stack
      setNavigationStack(prev => [...prev, {
        type: 'overview',
        data: { selectedProduct, selectedVariant, selectedLocation }
      }]);

      // Set drill-down context
      setDrillDownType(type);
      setDrillDownData(itemData);

      // Fetch detailed data based on drill-down type
      const queryParams = {};
      
      if (startDate) queryParams.startDate = format(startDate, 'yyyy-MM-dd');
      if (endDate) queryParams.endDate = format(endDate, 'yyyy-MM-dd');
      
      console.log("selectedLocation", selectedLocation);
      console.log("selectedProduct", selectedProduct);
      console.log("selectedVariant", selectedVariant);
      if(selectedLocation) {
        queryParams.locationId = selectedLocation._id;
      }

      if(selectedProduct) {
        queryParams.productId = selectedProduct._id;
      }
      
      if(selectedVariant) {
        queryParams.skus = selectedVariant.map(v => v.sku).join(',');
      }
      
      if (type === 'product') {
        queryParams.productIds = itemData.productId;
      } else if (type === 'location') {
        queryParams.locationId = itemData.location
      } else if (type === 'sku') {
        queryParams.sku = itemData.sku;
      }

      queryParams.skipPagination = true;
      const response = await superagent
        .get(`${backendUrl}/inventory/inventoryAccuracyTrends`)
        .query(queryParams)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      setDrillDownData({
        ...itemData,
        trendsData: response.body.data || [],
        type: type
      });

    } catch (error) {
      console.error('Error drilling down:', error);
      // Still show the modal but with error message
      setDrillDownData({
        ...itemData,
        trendsData: [],
        error: 'Failed to fetch trends data',
        type: type
      });
    }
  };

  

  const columns = useMemo(
    () => [
      columnHelper.accessor('product', {
        header: 'Product',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('sku', {
        header: 'SKU',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('locationName', {
        header: 'Location',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('systemOnHand', {
        header: 'System Inventory',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('physicalCount', {
        header: 'Physical Count',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('accuracy', {
        header: 'Accuracy (%)',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('variance', {
        header: 'Variance',
        cell: info => {
          const value = info.getValue();
          return (
            <span className={value >= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(value)}
            </span>
          );
        }
      }),
      columnHelper.accessor('auditDate', {
        header: 'Audit Date',
        cell: info => moment(info.getValue()).format('DD/MM/YYYY HH:mm')
      })
    ],
    []
  );

  // Header component
  const TableHeader = React.memo(() => {
    const getMinWidth = (header) => {
      switch(header) {
        case 'Product': return '200px';
        case 'SKU': return '120px';
        case 'Location': return '150px';
        case 'System Inventory': return '120px';
        case 'Physical Count': return '120px';
        case 'Accuracy (%)': return '100px';
        case 'Variance': return '100px';
        case 'Audit Date': return '150px';
        default: return '100px';
      }
    };

    return (
      <div className="flex font-semibold bg-gray-50 text-gray-900">
        {columns.map(column => (
          <div 
            key={column.header} 
            className="flex-1 px-4 py-3 text-sm"
            style={{ minWidth: getMinWidth(column.header) }}
          >
            {column.header}
          </div>
        ))}
      </div>
    );
  });


  const VirtualRow = useCallback(({ index, style }) => {
    const row = data[index];
    if (!row) return null;
    
    const getMinWidth = (header) => {
      switch(header) {
        case 'Product': return '200px';
        case 'SKU': return '120px';
        case 'Location': return '150px';
        case 'System Inventory': return '120px';
        case 'Physical Count': return '120px';
        case 'Accuracy (%)': return '100px';
        case 'Variance': return '100px';
        case 'Audit Date': return '150px';
        default: return '100px';
      }
    };
    
    return (
      <div 
        style={style} 
        className="flex border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => setSelectedAudit(row)}
      >
        {columns.map(column => (
          <div 
            key={`${row.auditId}-${column.header}`} 
            className="flex-1 px-4 py-3 text-sm text-gray-700 truncate"
            style={{ minWidth: getMinWidth(column.header) }}
          >
            {column.cell({ getValue: () => row[column.accessorKey] })}
          </div>
        ))}
      </div>
    );
  }, [data, columns]);

  // Audit details
  const renderAuditDetails = (audit) => {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Audit Details</h2>
          <p className="text-gray-600">Detailed information for selected audit record</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">Product</p>
              <p className="text-lg font-semibold text-gray-900">{audit.product}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">SKU</p>
              <p className="text-lg font-semibold text-gray-900">{audit.sku}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">Location</p>
              <p className="text-lg font-semibold text-gray-900">{audit.locationName}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">System Inventory</p>
              <p className="text-lg font-semibold text-gray-900">{audit.systemOnHand}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">Physical Count</p>
              <p className="text-lg font-semibold text-gray-900">{audit.physicalCount}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">Accuracy</p>
              <p className="text-lg font-semibold text-gray-900">{audit.accuracy}%</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">Variance</p>
              <p className={`text-lg font-semibold ${audit.variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {Math.abs(audit.variance)}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-1">Audit Date</p>
              <p className="text-lg font-semibold text-gray-900">{moment(audit.auditDate).format('DD/MM/YYYY HH:mm')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Location selector
  const renderLocationSelector = () => {
    return (
      <>
        <label className="block text-sm font-semibold text-gray-900 mb-0">Location</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent !mt-0"
          value={selectedLocation?._id || ""}
          onChange={(e) => {
            const locationId = e.target.value;
            console.log('Selected location ID:', locationId);
            console.log('Available locations:', locations);
            const location = locations.find(loc => loc._id === locationId);
            console.log('Found location:', location);
            setSelectedLocation(location || null);
          }}
        >
          <option value="">All Locations</option>
          {Array.isArray(locations) && locations.length > 0 ? (
            locations.map(location => (
              <option key={location._id} value={location._id}>
                {location.display_name || location.name}
              </option>
            ))
          ) : (
            <option disabled>No locations available</option>
          )}
        </select>
      </>
    );
  };

  return (
    <div className="page-content">
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Inventory Accuracy Report</h1>
                <p className="text-gray-600">Monitor inventory accuracy and variances across locations and products</p>
              </div>
            
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white">
          
          <Tabs defaultValue="skuLevel" value={activeTab} onValueChange={setActiveTab}>
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 bg-gray-50">
              <div className="px-6 py-4">
                <div className="overflow-x-auto">
                  <TabsList className="bg-white border border-gray-200 p-1 rounded-lg shadow-sm min-w-max">
                      <TabsTrigger value="skuLevel" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white px-4 py-2 text-sm font-medium whitespace-nowrap">SKU Level</TabsTrigger>
                      <TabsTrigger value="productLevel" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white px-4 py-2 text-sm font-medium whitespace-nowrap">Product Level</TabsTrigger>
                      <TabsTrigger value="locationLevel" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white px-4 py-2 text-sm font-medium whitespace-nowrap">Location Level</TabsTrigger>
                      <TabsTrigger value="timeLevel" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white px-4 py-2 text-sm font-medium whitespace-nowrap">Time Trends</TabsTrigger>
                      <TabsTrigger value="dashboard" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white px-4 py-2 text-sm font-medium whitespace-nowrap">Dashboard</TabsTrigger>
                      <TabsTrigger value="alerts" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white px-4 py-2 text-sm font-medium whitespace-nowrap">Alerts</TabsTrigger>
                      
                  </TabsList>
                </div>
              </div>
            </div>
            
               
                {activeTab !== 'alerts' && (
                  <div className="px-6 py-6 bg-white border-b border-gray-100">
                    <div className={`grid grid-cols-1 gap-6 items-start ${activeTab === 'skuLevel' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
                      {/* Product/SKU Filter */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-900">Product/SKU</label>
                        <div className="relative">
                          <ProductVariantMultiSelector
                            products={products}
                            hidePersonalisationOptions={true}
                            selectedProduct={selectedProduct}
                            setSelectedProduct={setSelectedProduct}
                            selectedVariant={selectedVariant}
                            setSelectedVariant={setSelectedVariant}
                            isMulti={true}
                            showSku={true}
                          />
                        </div>
                      </div>
                      
                      {/* Location Filter */}
                      <div className="space-y-2">
                        {renderLocationSelector()}
                      </div>
                      
                      {/* Date Range Filter */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-900">Date Range</label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full justify-start text-left font-normal border-gray-300 hover:border-gray-400 bg-white"
                                >
                                  {startDate ? (
                                    format(startDate, "dd/MM/yyyy")
                                  ) : (
                                    <span className="text-gray-500">From date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 text-gray-400" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={startDate}
                                  onSelect={setStartDate}
                                  defaultMonth={startDate || new Date()}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          
                          <div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full justify-start text-left font-normal border-gray-300 hover:border-gray-400 bg-white"
                                >
                                  {endDate ? (
                                    format(endDate, "dd/MM/yyyy")
                                  ) : (
                                    <span className="text-gray-500">To date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 text-gray-400" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={endDate}
                                  onSelect={setEndDate}
                                  defaultMonth={endDate || new Date()}
                                  initialFocus
                                  disabled={(date) => startDate && date < startDate}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                      </div>
                      
                      {/* Accuracy Range Filter - Only for SKU Level Analysis */}
                      {activeTab === 'skuLevel' && (
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-900">Accuracy Range (%)</label>
                          
                            <DualRangeSlider
                              value={tempAccuracyRange}
                              onChange={handleAccuracyRangeChange}
                              min={0}
                              max={100}
                            />
                          
                        </div>
                      )}
                    </div>
                  </div>
                )}
            
               
                {activeTab !== 'alerts' && (
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <Button 
                          onClick={handleResetFilters}
                          variant="outline"
                          className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                        >
                          Clear Filters
                        </Button>
                        
                        <Button 
                          onClick={() => setShowToleranceModal(true)}
                          variant="outline"
                          className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                        >
                          ⚙️ Tolerance Settings
                        </Button>
                        
                        <select
                          value={pagination.pageSize}
                          onChange={e => setPagination(prev => ({ ...prev, pageSize: Number(e.target.value) }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent">
                          {[50, 100, 200, 500].map(size => (
                            <option key={size} value={size}>
                              Show {size}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => {
                            const now = new Date();
                            const oneWeekAgo = new Date();
                            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                            setStartDate(oneWeekAgo);
                            setEndDate(now);
                          }}
                          variant="outline"
                          className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm"
                        >
                          Last Week
                        </Button>
                        <Button 
                          onClick={() => {
                            const now = new Date();
                            const oneMonthAgo = new Date();
                            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                            setStartDate(oneMonthAgo);
                            setEndDate(now);
                          }}
                          variant="outline"
                          className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm"
                        >
                          Last Month
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
            
                <TabsContent value="skuLevel" className="p-0">
                  <div className="bg-white">
                    {/* Table Header */}
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">SKU Level Analysis</h3>
                      <p className="text-sm text-gray-600 mt-1">Detailed accuracy metrics for individual SKUs</p>
                    </div>
                    
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <div className="min-w-[1000px]">
                        <div className="border-b border-gray-200">
                          <TableHeader />
                        </div>
                        
                        <div style={{ height: 'calc(100vh - 450px)', minHeight: '400px' }}>
                        {loading && data.length === 0 ? (
                          <div className="flex flex-col justify-center items-center h-full py-16">
                            <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mb-4"></div>
                            <p className="text-gray-600 font-medium">Loading data...</p>
                          </div>
                        ) : data.length === 0 ? (
                          <div className="flex flex-col justify-center items-center h-full py-16">
                            <div className="text-6xl mb-4 text-gray-300">📊</div>
                            <p className="text-gray-600 font-medium mb-2">No data available</p>
                            <p className="text-sm text-gray-500">Try adjusting your filters or date range</p>
                          </div>
                        ) : (
                          <AutoSizer>
                            {({ height, width }) => (
                              <List
                                height={height}
                                itemCount={data.length}
                                itemSize={50}
                                width={width}
                              >
                                {VirtualRow}
                              </List>
                            )}
                          </AutoSizer>
                        )}
                        </div>
                      </div>
                    </div>
                    
                    {hasMore && (
                      <div className="flex justify-center py-6 border-t border-gray-200">
                        <Button 
                          onClick={handleLoadMore} 
                          disabled={loading}
                          className="bg-gray-900 hover:bg-gray-800 text-white font-medium px-6 py-2"
                        >
                          {loading ? (
                            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Loading...</>
                          ) : 'Load More'}
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
            
                <TabsContent value="productLevel" className="p-0">
                  <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Product Level Analysis</h3>
                      <p className="text-sm text-gray-600 mt-1">Accuracy performance across product categories</p>
                    </div>
                    
                    <div className="p-6">
                      {loading ? (
                        <div className="flex flex-col justify-center items-center h-64">
                          <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mb-4"></div>
                          <p className="text-gray-600 font-medium">Loading product accuracy data...</p>
                        </div>
                      ) : productAccuracy.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-64">
                          <div className="text-6xl mb-4 text-gray-300">📦</div>
                          <p className="text-gray-600 font-medium mb-2">No product accuracy data available</p>
                          <p className="text-sm text-gray-500">Try adjusting your filters or date range</p>
                        </div>
                      ) : (
                        <ProductAccuracyChart 
                          productAccuracy={productAccuracy}
                          handleDrillDown={handleDrillDown}
                        />
                      )}
                    </div>
                  </div>
                </TabsContent>
            
                <TabsContent value="locationLevel" className="p-0">
                  <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Location Level Analysis</h3>
                      <p className="text-sm text-gray-600 mt-1">Accuracy performance across different locations</p>
                    </div>
                    
                    <div className="p-6">
                      {loading ? (
                        <div className="flex flex-col justify-center items-center h-64">
                          <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mb-4"></div>
                          <p className="text-gray-600 font-medium">Loading location accuracy data...</p>
                        </div>
                      ) : locationAccuracy.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-64">
                          <div className="text-6xl mb-4 text-gray-300">🏢</div>
                          <p className="text-gray-600 font-medium mb-2">No location accuracy data available</p>
                          <p className="text-sm text-gray-500">Try adjusting your filters or date range</p>
                        </div>
                      ) : (
                        <LocationAccuracyChart 
                          locationAccuracy={locationAccuracy}
                          handleDrillDown={handleDrillDown}
                        />
                      )}
                    </div>
                  </div>
                </TabsContent>
            
                <TabsContent value="timeLevel" className="p-0">
                  <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Time Trends Analysis</h3>
                      <p className="text-sm text-gray-600 mt-1">Accuracy trends over time periods</p>
                    </div>
                    
                    <div className="p-6">
                      {loading ? (
                        <div className="flex flex-col justify-center items-center h-64">
                          <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mb-4"></div>
                          <p className="text-gray-600 font-medium">Loading time-based report data...</p>
                        </div>
                      ) : timeBasedReport.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-64">
                          <div className="text-6xl mb-4 text-gray-300">📈</div>
                          <p className="text-gray-600 font-medium mb-2">No time-based report data available</p>
                          <p className="text-sm text-gray-500">Try adjusting your filters or date range</p>
                        </div>
                      ) : (
                        <TimeBasedReportChart 
                          timeBasedReport={timeBasedReport}
                          activeChartType={activeChartType}
                          setActiveChartType={setActiveChartType}
                        />
                      )}
                    </div>
                  </div>
                </TabsContent>
          
                  <TabsContent value="dashboard" className="p-0">
                  <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Dashboard Overview</h3>
                      <p className="text-sm text-gray-600 mt-1">Comprehensive inventory accuracy insights</p>
                    </div>
                    
                    <div className="p-6">
                      <div className="space-y-8">
                        
                        {/* Key Metrics Summary */}
                        {valueWeightedAccuracy && toleranceMetrics && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg hover:shadow-sm transition-shadow">
                              <div className="text-sm font-medium text-gray-600 mb-2">Value Weighted Accuracy</div>
                              <div className="text-3xl font-bold text-gray-900">
                                {valueWeightedAccuracy.toFixed(1)}%
                              </div>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-lg hover:shadow-sm transition-shadow">
                              <div className="text-sm font-medium text-gray-600 mb-2">Within Tolerance</div>
                              <div className="text-3xl font-bold text-emerald-700">
                                {toleranceMetrics.withinTolerance || 0}
                              </div>
                              <div className="text-xs text-emerald-600 mt-1">
                                {toleranceMetrics.toleranceAccuracy?.toFixed(1)}% accuracy
                              </div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg hover:shadow-sm transition-shadow">
                              <div className="text-sm font-medium text-gray-600 mb-2">Near Tolerance</div>
                              <div className="text-3xl font-bold text-amber-700">
                                {toleranceMetrics.nearTolerance || 0}
                              </div>
                            </div>
                            <div className="bg-red-50 border border-red-200 p-6 rounded-lg hover:shadow-sm transition-shadow">
                              <div className="text-sm font-medium text-gray-600 mb-2">Outside Tolerance</div>
                              <div className="text-3xl font-bold text-red-700">
                                {toleranceMetrics.outsideTolerance || 0}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Main Visualizations Grid */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                          
                          {/* Hit Rate Gauges */}
                          <Card className="border border-gray-200 shadow-sm">
                            <CardContent className="p-6">
                              {hitRateMetrics ? (
                                <HitRateGauges 
                                  hitRateMetrics={hitRateMetrics}
                                  handleDrillDown={handleDrillDown}
                                />
                              ) : (
                                <div className="h-64 flex flex-col justify-center items-center">
                                  <div className="text-5xl mb-4 text-gray-300">🎯</div>
                                  <p className="text-gray-600 font-medium">No hit rate data available</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                  
                          {/* Enhanced Trend Chart */}
                          <Card className="border border-gray-200 shadow-sm">
                            <CardContent className="p-6">
                              {trendMetrics ? (
                                <EnhancedTrendChart 
                                  trendMetrics={trendMetrics}
                                />
                              ) : (
                                <div className="h-64 flex flex-col justify-center items-center">
                                  <div className="text-5xl mb-4 text-gray-300">📊</div>
                                  <p className="text-gray-600 font-medium">No trend metrics data available</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                  
                          
                          <Card className="xl:col-span-2 border border-gray-200 shadow-sm">
                            <CardContent className="p-6">
                              {varianceAnalysis && problemAnalysis?.topInaccurateLocations ? (
                                <VarianceHeatMap 
                                  varianceAnalysis={varianceAnalysis}
                                  problemAnalysis={problemAnalysis}
                                  handleDrillDown={handleDrillDown}
                                />
                              ) : (
                                <div className="h-64 flex flex-col justify-center items-center">
                                  <div className="text-5xl mb-4 text-gray-300">🗺️</div>
                                  <p className="text-gray-600 font-medium">No variance data available</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                  
                          {/* Product Accuracy Chart - Full Width for Better Visibility */}
                          <Card className="xl:col-span-2 border border-gray-200 shadow-sm">
                            <CardContent className="p-6">
                              {productAccuracy.length > 0 ? (
                                <ProductAccuracyChart 
                                  productAccuracy={productAccuracy}
                                  handleDrillDown={handleDrillDown}
                                />
                              ) : (
                                <div className="h-64 flex flex-col justify-center items-center">
                                  <div className="text-5xl mb-4 text-gray-300">📦</div>
                                  <p className="text-gray-600 font-medium">No product accuracy data available</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                  
                          {/* Location Accuracy Chart */}
                          <Card className="xl:col-span-2 border border-gray-200 shadow-sm">
                            <CardContent className="p-6">
                              {locationAccuracy.length > 0 ? (
                                <LocationAccuracyChart 
                                  locationAccuracy={locationAccuracy}
                                  handleDrillDown={handleDrillDown}
                                />
                              ) : (
                                <div className="h-64 flex flex-col justify-center items-center">
                                  <div className="text-5xl mb-4 text-gray-300">🏢</div>
                                  <p className="text-gray-600 font-medium">No location accuracy data available</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                  
                          {/* Top Issues */}
                          <Card className="xl:col-span-2 border border-gray-200 shadow-sm">
                            <CardContent className="p-6">
                              {problemAnalysis ? (
                                <TopIssuesLists 
                                  problemAnalysis={problemAnalysis}
                                  activeIssueTab={activeIssueTab}
                                  setActiveIssueTab={setActiveIssueTab}
                                />
                              ) : (
                                <div className="h-64 flex flex-col justify-center items-center">
                                  <div className="text-5xl mb-4 text-gray-300">⚠️</div>
                                  <p className="text-gray-600 font-medium">No problem analysis data available</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                          
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="alerts" className="p-0">
                  <div className="bg-white">
                    
                    <div className="p-6">
                      <AlertDashboard />
                    </div>
                  </div>
                </TabsContent>

               
          </Tabs>
        </div>
      </div>

      {/* Audit Details Dialog */}
      <Dialog
        open={!!selectedAudit}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedAudit(null);
          }
        }}
        size="lg">
        <DialogContent className="z-[1600] max-h-[80vh] overflow-y-auto w-[95vw] max-w-[900px] mx-auto bg-white border border-gray-200 shadow-xl">
          {selectedAudit && renderAuditDetails(selectedAudit)}
        </DialogContent>
      </Dialog>

      {/* Detailed Investigation Dialog */}
      <Dialog
        open={!!drillDownData}
        onOpenChange={(open) => {
          if (!open) {
            setDrillDownData(null);
            setDrillDownType(null);
          }
        }}
        size="xl">
        <DialogContent className="z-[1600] max-h-[90vh] overflow-y-auto w-[95vw] max-w-[1200px] mx-auto bg-white border border-gray-200 shadow-xl">
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Detailed Investigation</h2>
              <p className="text-gray-600">In-depth analysis and trends</p>
            </div>
            {drillDownData && drillDownType && (
              <DetailedInvestigationView 
                data={drillDownData} 
                type={drillDownType} 
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tolerance Settings Modal */}
      <ToleranceSettingsModal
        isOpen={showToleranceModal}
        onClose={() => setShowToleranceModal(false)}
      />
        </div>
      </div>
    
  );
};

export default InventoryAccuracyPage;
